import { formOptions } from '@tanstack/react-form';
import { z } from 'zod';

import { WORKFLOW_CHANNEL_TYPE } from '@/types/workflowConst';

import { notificationChannelSchema } from './notification-channel-schema';
import { CONDITION_OPERATOR } from './signal-alert-constants';

export const signalAlertFormSchema = z.object({
  title: z.string().min(1, { message: 'Please enter a title' }),
  signals: z.array(z.string()),
  duration: z
    .number()
    .min(1, { message: 'Duration must be at least 1 day' })
    .max(7, { message: 'Duration must be at most 7 days' }),
  feedbackCountCondition: z.object({
    enabled: z.boolean(),
    threshold: z.number().min(0, { message: 'Threshold must be at least 0' }),
  }),
  conditionOperator: z.enum(CONDITION_OPERATOR),
  changeRateCondition: z.object({
    enabled: z.boolean(),
    threshold: z.number().min(0, { message: 'Threshold must be at least 0' }),
  }),
  filter: z.any().optional(),
  channel: notificationChannelSchema,
});

export type SignalAlertFormData = z.infer<typeof signalAlertFormSchema>;

export const signalAlertFormDefaultValues: SignalAlertFormData = {
  title: '',
  signals: [],
  duration: 7,
  feedbackCountCondition: {
    enabled: true,
    threshold: 100,
  },
  conditionOperator: CONDITION_OPERATOR.AND,
  changeRateCondition: {
    enabled: false,
    threshold: 10,
  },
  filter: undefined,
  channel: {
    type: WORKFLOW_CHANNEL_TYPE.SLACK,
    slack_webhook_id: '',
  },
};

// 폼 검증 함수 (duration만 number로 변환)
// GlobalFormValidationError 형식으로 반환하여 field별 에러 분배
const validateSignalAlertForm = ({ value }: { value: SignalAlertFormData }) => {
  const coercedValue = {
    ...value,
    duration: Number(value.duration),
  };

  const result = signalAlertFormSchema.safeParse(coercedValue);
  if (result.success) return undefined;

  const fields: Record<string, string> = {};
  for (const issue of result.error.issues) {
    const path = issue.path.join('.');
    if (!fields[path]) fields[path] = issue.message;
  }
  return { fields };
};

export const signalAlertFormOptions = formOptions({
  defaultValues: signalAlertFormDefaultValues,
  validators: {
    onSubmit: validateSignalAlertForm,
  },
});
